import {
  type Post,
  type PostList,
  type Product,
  type ProductTag,
  planAllows,
  type Shop,
} from "@brandwall/contracts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router";
import { ApiError, api, del, post, put, timeAgo } from "../api";
import { useWorkspace } from "../components/Shell";
import { useToast } from "../components/Toast";

function price(p: Product) {
  if (p.price === null) return "";
  try {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: p.currency ?? "EUR",
      maximumFractionDigits: Number.isInteger(p.price) ? 0 : 2,
    }).format(p.price);
  } catch {
    return String(p.price);
  }
}

function ShopCard({ shop }: { shop: Shop }) {
  const { ws } = useParams();
  const workspace = useWorkspace();
  const qc = useQueryClient();
  const toast = useToast();
  const [url, setUrl] = useState(workspace.website ?? "");
  const [error, setError] = useState<string | null>(null);
  const connect = useMutation({
    mutationFn: () => post<Shop & { result: { summary: string } }>(`/v1/w/${ws}/shop`, { url }),
    onSuccess: (d) => {
      qc.setQueryData(["shop", ws], d);
      qc.invalidateQueries({ queryKey: ["products", ws] });
      toast(d.result.summary);
      setError(null);
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : "We couldn't connect your shop."),
  });
  if (!shop.connected) {
    return (
      <section className="card stack">
        <div>
          <h2>Connect your shop</h2>
          <p className="muted">
            Connect your shop to tag products in your posts, so shoppers can buy what they see. On
            Shopify? Enter your shop's web address. Another shop system? Paste the link to your
            product feed. Most shops can export one for Google Shopping.
          </p>
        </div>
        {error && (
          <div className="error" role="alert">
            {error}
          </div>
        )}
        <div className="row wrap">
          <input
            className="input spacer"
            style={{ minWidth: 220 }}
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="fjellbu.dk, or your product feed link"
            aria-label="Shop web address or product feed link"
          />
          <button
            type="button"
            className="btn"
            onClick={() => connect.mutate()}
            disabled={connect.isPending || workspace.role !== "owner"}
          >
            {connect.isPending ? "Connecting…" : "Connect shop"}
          </button>
        </div>
      </section>
    );
  }
  return (
    <section className="card row wrap">
      <span className={`pill ${shop.status === "connected" ? "ok" : "berry"}`}>
        <span
          className="dot"
          style={shop.status === "connected" ? {} : { background: "var(--berry)" }}
        />{" "}
        {shop.kind === "shopify" ? "Shopify shop" : "Product feed"}{" "}
        {shop.status === "connected" ? "connected" : "needs attention"}
      </span>
      <span className="muted small spacer">
        {shop.productCount} products
        {shop.lastSyncedAt ? ` · updated ${timeAgo(shop.lastSyncedAt)}` : ""}
        {shop.lastError ? ` · ${shop.lastError}` : ""}
      </span>
      <button
        type="button"
        className="btn secondary sm"
        onClick={async () => {
          const r = await post<{ summary: string }>(`/v1/w/${ws}/shop/sync`, {});
          toast(r.summary);
        }}
      >
        Update products
      </button>
    </section>
  );
}

function Tagger({ p, canTag }: { p: Post; canTag: boolean }) {
  const { ws } = useParams();
  const qc = useQueryClient();
  const toast = useToast();
  const key = ["post-products", ws, p.id];
  const tags = useQuery({
    queryKey: key,
    queryFn: () => api<{ tags: ProductTag[] }>(`/v1/w/${ws}/posts/${p.id}/products`),
  });
  const [pending, setPending] = useState<{ x: number; y: number } | null>(null);
  const [q, setQ] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState(false);
  const search = useQuery({
    queryKey: ["products", ws, q],
    queryFn: () =>
      api<{ products: Product[]; total: number }>(
        `/v1/w/${ws}/products?q=${encodeURIComponent(q)}&limit=20`,
      ),
  });
  const imgRef = useRef<HTMLImageElement>(null);
  const areaRef = useRef<HTMLDivElement>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset when another post is picked
  useEffect(() => {
    setPending(null);
    setError(null);
    setPreview(false);
  }, [p.id]);

  const confirmed = (tags.data?.tags ?? []).filter((t) => t.status === "confirmed");
  const suggested = (tags.data?.tags ?? []).filter((t) => t.status === "suggested");

  useEffect(() => {
    const el = areaRef.current;
    if (!el || !canTag) return;
    const place = (e: MouseEvent) => {
      if ((e.target as HTMLElement).closest("button")) return;
      const r = imgRef.current!.getBoundingClientRect();
      setPending({ x: (e.clientX - r.left) / r.width, y: (e.clientY - r.top) / r.height });
    };
    el.addEventListener("click", place);
    return () => el.removeEventListener("click", place);
  }, [canTag]);
  const tag = async (productId: string, at: { x: number; y: number }) => {
    try {
      const r = await put<{ tags: ProductTag[]; result: { summary: string } }>(
        `/v1/w/${ws}/posts/${p.id}/products/${productId}`,
        at,
      );
      qc.setQueryData(key, { tags: r.tags });
      toast(r.result.summary);
      setPending(null);
      setError(null);
      qc.invalidateQueries({ queryKey: ["posts", ws] });
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Something went wrong.");
    }
  };
  const remove = async (productId: string) => {
    const r = await del<{ tags: ProductTag[]; result: { summary: string } }>(
      `/v1/w/${ws}/posts/${p.id}/products/${productId}`,
    );
    qc.setQueryData(key, { tags: r.tags });
  };
  const suggest = async () => {
    try {
      const r = await post<{ tags: ProductTag[] }>(
        `/v1/w/${ws}/posts/${p.id}/products/suggest`,
        {},
      );
      qc.setQueryData(key, r);
      if (!r.tags.some((t) => t.status === "suggested"))
        toast("No suggestions for this post. Click a product in the photo to tag it yourself.");
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Something went wrong.");
    }
  };

  return (
    <div className="split wide-side">
      <section className="card stack">
        <div className="row">
          <b className="spacer">@{p.creator || "creator"}</b>
          <span className="small muted">
            {canTag ? "Click a product in the photo to tag it" : ""}
          </span>
        </div>
        {error && (
          <div className="error" role="alert">
            {error}
          </div>
        )}
        <div style={{ textAlign: "center" }}>
          {/* Products can also be tagged with the keyboard: Suggest, then Confirm. */}
          <div className="tagger" ref={areaRef}>
            {p.previewUrl && (
              <img
                ref={imgRef}
                src={p.previewUrl}
                alt={`Post by @${p.creator}`}
                draggable={false}
              />
            )}
            {confirmed.map((t, i) => (
              <span
                key={t.id}
                className="tdot"
                style={{ left: `${t.x * 100}%`, top: `${t.y * 100}%` }}
                title={t.title}
              >
                {i + 1}
              </span>
            ))}
            {suggested.map((t) => (
              <span key={t.id}>
                <span
                  className="tdot suggested"
                  style={{ left: `${t.x * 100}%`, top: `${t.y * 100}%` }}
                >
                  ✦
                </span>
                <span
                  className="suggest"
                  style={{ left: `${Math.min(t.x, 0.55) * 100}%`, top: `${t.y * 100}%` }}
                >
                  ✦ Suggested: {t.title}
                  {t.confidence ? ` · ${t.confidence}%` : ""}
                  {canTag && (
                    <>
                      <button
                        type="button"
                        className="btn sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          tag(t.id, { x: t.x, y: t.y });
                        }}
                      >
                        Confirm
                      </button>
                      <button
                        type="button"
                        className="btn secondary sm"
                        aria-label={`Dismiss suggestion ${t.title}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          remove(t.id);
                        }}
                      >
                        ✕
                      </button>
                    </>
                  )}
                </span>
              </span>
            ))}
            {pending && (
              <span
                className="tdot new"
                style={{ left: `${pending.x * 100}%`, top: `${pending.y * 100}%` }}
              >
                +
              </span>
            )}
          </div>
        </div>
        <div className="row wrap">
          {canTag && (
            <button type="button" className="btn secondary sm" onClick={suggest}>
              ✦ Suggest products
            </button>
          )}
          <button type="button" className="btn secondary sm" onClick={() => setPreview(!preview)}>
            {preview ? "Hide shopper preview" : "How shoppers see it"}
          </button>
        </div>
      </section>

      <div className="stack">
        {pending && (
          <section className="card stack">
            <h2>Which product is this?</h2>
            <input
              className="input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search your products"
              aria-label="Search products"
            />
            <div className="plist">
              {search.data?.products.map((pr) => (
                <button
                  key={pr.id}
                  type="button"
                  className="prod"
                  onClick={() => tag(pr.id, pending)}
                >
                  {pr.imageUrl ? (
                    <img className="thumb" src={pr.imageUrl} alt="" />
                  ) : (
                    <span className="thumb" />
                  )}
                  <span>
                    <b>{pr.title}</b>
                    <span>
                      {[pr.variantTitle, price(pr)].filter(Boolean).join(" · ")}
                      {!pr.available ? " · sold out" : ""}
                    </span>
                  </span>
                </button>
              ))}
            </div>
            <button type="button" className="btn link small" onClick={() => setPending(null)}>
              Cancel
            </button>
          </section>
        )}
        <section className="card stack">
          <h2>Tagged in this post</h2>
          {confirmed.length === 0 && <p className="muted">No products tagged yet.</p>}
          <div className="plist">
            {confirmed.map((t, i) => (
              <div key={t.id} className="prod">
                <i
                  className="tdot"
                  style={{
                    position: "static",
                    transform: "none",
                    flex: "none",
                    fontStyle: "normal",
                  }}
                >
                  {i + 1}
                </i>
                {t.imageUrl ? (
                  <img className="thumb" src={t.imageUrl} alt="" />
                ) : (
                  <span className="thumb" />
                )}
                <span className="spacer">
                  <b>{t.title}</b>
                  <span>
                    {[t.variantTitle, price(t)].filter(Boolean).join(" · ")}
                    {!t.available ? " · sold out, hidden from shoppers" : ""}
                  </span>
                </span>
                {canTag && (
                  <button type="button" className="btn link small" onClick={() => remove(t.id)}>
                    Remove
                  </button>
                )}
              </div>
            ))}
          </div>
        </section>
        {preview && (
          <section className="card stack">
            <h2>How shoppers see it</h2>
            <div className="row" style={{ alignItems: "flex-start", flexWrap: "wrap" }}>
              <div className="tagger" style={{ cursor: "default", maxWidth: 220 }}>
                {p.previewUrl && <img src={p.previewUrl} alt="" />}
                {confirmed
                  .filter((t) => t.available)
                  .map((t, i) => (
                    <span
                      key={t.id}
                      className="tdot"
                      style={{ left: `${t.x * 100}%`, top: `${t.y * 100}%` }}
                    >
                      {i + 1}
                    </span>
                  ))}
              </div>
              <div className="stack spacer" style={{ minWidth: 200, gap: 8 }}>
                <b>Shop this look</b>
                <span className="small muted">@{p.creator}</span>
                {confirmed
                  .filter((t) => t.available)
                  .map((t) => (
                    <div key={t.id} className="row">
                      <span className="spacer">
                        <b>{t.title}</b> <span className="muted">{price(t)}</span>
                      </span>
                      <span className="btn sm">Add to bag</span>
                    </div>
                  ))}
              </div>
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

export function Products() {
  const { ws } = useParams();
  const workspace = useWorkspace();
  const [params, setParams] = useSearchParams();
  const shop = useQuery({ queryKey: ["shop", ws], queryFn: () => api<Shop>(`/v1/w/${ws}/shop`) });
  const approved = useQuery({
    queryKey: ["posts", ws, "live", "products"],
    queryFn: () => api<PostList>(`/v1/w/${ws}/posts?tab=live&limit=60`),
  });
  const waiting = useQuery({
    queryKey: ["posts", ws, "approved", "products"],
    queryFn: () => api<PostList>(`/v1/w/${ws}/posts?tab=approved&limit=60`),
  });
  const catalog = useQuery({
    queryKey: ["products", ws, "", "all"],
    queryFn: () => api<{ products: Product[]; total: number }>(`/v1/w/${ws}/products?limit=8`),
    enabled: Boolean(shop.data?.connected),
  });
  const posts = useMemo(
    () =>
      [...(approved.data?.posts ?? []), ...(waiting.data?.posts ?? [])].filter((p) => p.previewUrl),
    [approved.data, waiting.data],
  );
  const selected = posts.find((p) => p.id === params.get("p")) ?? posts[0];
  const canTag = workspace.role !== "viewer" && planAllows(workspace.plan, "product_dots");

  if (!shop.data) return <p className="muted">Loading…</p>;
  return (
    <div className="stack" style={{ gap: 18 }}>
      <ShopCard shop={shop.data} />
      {!planAllows(workspace.plan, "product_dots") && (
        <div className="card row wrap">
          <p className="spacer">
            <b>Tagging products is on the Pro plan.</b> Shoppers see the products in each post and
            can go straight to them in your shop.
          </p>
          <Link className="btn" to={`/w/${ws}/settings?tab=plan`}>
            See plans
          </Link>
        </div>
      )}
      {shop.data.connected && posts.length > 0 && (
        <>
          <div className="strip" role="group" aria-label="Approved posts">
            {posts.map((p) => (
              <button
                key={p.id}
                type="button"
                aria-pressed={p.id === selected?.id}
                aria-label={`Post by @${p.creator}${p.productCount ? `, ${p.productCount} products tagged` : ""}`}
                onClick={() => setParams({ p: p.id }, { replace: true })}
              >
                <img src={p.previewUrl!} alt="" />
                {p.productCount > 0 && <span className="n">{p.productCount}</span>}
              </button>
            ))}
          </div>
          {selected && <Tagger p={selected} canTag={canTag} />}
        </>
      )}
      {shop.data.connected && posts.length === 0 && (
        <p className="muted">
          Approved posts show up here. Approve a few in your inbox, then tag the products in them.
        </p>
      )}
      {catalog.data && catalog.data.total > 0 && (
        <section className="card stack">
          <div className="row">
            <h2 className="spacer">Your products</h2>
            <span className="small muted">{catalog.data.total} products</span>
          </div>
          <div className="plist">
            {catalog.data.products.map((pr) => (
              <div key={pr.id} className="prod">
                {pr.imageUrl ? (
                  <img className="thumb" src={pr.imageUrl} alt="" />
                ) : (
                  <span className="thumb" />
                )}
                <span>
                  <b>{pr.title}</b>
                  <span>
                    {[pr.variantTitle, price(pr)].filter(Boolean).join(" · ")}
                    {!pr.available ? " · sold out" : ""}
                  </span>
                </span>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
